import axios from "../lib/axios.js";
import { isExpectedOwnerLoginAsCreateCode } from "../utils/ownerLoginAsCreateCodes.js";

const OK_FALSE_MESSAGE = "createOwnerLoginAsClientLink returned ok:false";
const REQUEST_FAILED_MESSAGE = "createOwnerLoginAsClientLink request failed";

/**
 * Safe LaunchError arguments for a login-as link failure.
 * The error carries a static message plus code, status, and hasEmail only.
 * Email, token, hash, and URL stay off the object LaunchError logs and sends.
 */
export const buildOwnerLoginAsLaunchErrorArgs = ({
  code,
  status = null,
  name,
  cancelled = false,
  skipSentry = false,
  message,
  hasScope,
}) => {
  const safeError = new Error(message);
  if (typeof code === "string" && code) safeError.code = code;
  if (typeof name === "string" && name && name !== "Error") {
    safeError.name = name;
  }
  if (cancelled === true) safeError.cancelled = true;

  return {
    error: safeError,
    showAlert: false,
    ignoreAlertByErrors: [],
    sentryMeta: {
      functionName: "startCreateOwnerLoginAsClientLink",
      context: {
        hasScope: hasScope === true,
        code: code || null,
        status,
      },
      tags: { operation: "create_owner_login_as_client_link" },
      skipSentry: skipSentry === true,
    },
  };
};

const requestOwnerLoginAsClientLink = async ({
  agencyID,
  token,
  url,
  dispatch,
  launchError,
  axiosClient = axios,
}) => {
  const hasScope = Boolean(agencyID);
  const reportFailure = (details) => {
    const { error, showAlert, ignoreAlertByErrors, sentryMeta } =
      buildOwnerLoginAsLaunchErrorArgs(details);
    return dispatch(
      launchError(error, showAlert, ignoreAlertByErrors, sentryMeta)
    );
  };

  try {
    const { data } = await axiosClient({
      method: "POST",
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {
        agencyID,
      },
    });

    if (data?.ok) {
      return {
        ok: true,
        data: data.data,
      };
    }

    const code = typeof data?.code === "string" ? data.code : null;
    if (!isExpectedOwnerLoginAsCreateCode(code)) {
      await reportFailure({
        code,
        hasScope,
        status: null,
        message: OK_FALSE_MESSAGE,
      });
    }

    return {
      ok: false,
      message: "Failed to create login link",
      code,
    };
  } catch (error) {
    const responseCode = error?.response?.data?.code;
    const code =
      typeof responseCode === "string" && responseCode
        ? responseCode
        : typeof error?.code === "string"
          ? error.code
          : null;
    const errorFormatted = await reportFailure({
      code,
      status: error?.response?.status ?? null,
      name: error?.name,
      cancelled: error?.cancelled === true,
      skipSentry: isExpectedOwnerLoginAsCreateCode(code),
      hasScope,
      message: REQUEST_FAILED_MESSAGE,
    });
    return {
      ok: false,
      message: errorFormatted?.message,
      code: errorFormatted?.code || code,
    };
  }
};

/**
 * Link request used by startCreateOwnerLoginAsClientLink once the owner token
 * and function URL are known. launchError is LaunchError in the app and a
 * fake in tests. axiosClient is the real client unless a test passes a mock.
 */
export const startRequestOwnerLoginAsClientLink =
  ({ agencyID, token, url, axiosClient, launchError }) =>
  (dispatch) =>
    requestOwnerLoginAsClientLink({
      agencyID,
      token,
      url,
      axiosClient,
      dispatch,
      launchError,
    });
